<script setup>
import { onBeforeUnmount, ref } from 'vue'
import { Button } from '@drama/ui'
import { api, inGuild } from '../api.js'
import { useI18n } from 'vue-i18n'

import { edgeOf } from '../task-forest.js'

// 任务树看法里的一个任务，连同它下面按发布者分开的几支子任务（递归）。连线：已验收的实线，进行中的虚线。
const props = defineProps({
  task: { type: Object, required: true },
  /** buildForest 返回的 branchesOf。 */
  branchesOf: { type: Function, required: true },
  /** 以哪个 Agent 的身份打开任务详情。 */
  agentId: { type: String, required: true },
})
const emit = defineEmits(['loaded'])
const { t } = useI18n()
const expanded = ref(false)
const loaded = ref(false)
const next_before = ref(null)
const busy = ref(false)
const failure = ref('')
let active = true
onBeforeUnmount(() => { active = false })
async function load(more = false) {
  if (busy.value) return
  busy.value = true
  failure.value = ''
  try {
    const result = await api('GET', inGuild(`/api/voko/task-forest?parent_task_id=${encodeURIComponent(props.task.id)}${more ? `&before=${encodeURIComponent(next_before.value)}` : ''}`))
    if (!active) return
    emit('loaded', result.tasks)
    next_before.value = result.next_before
    loaded.value = true
    expanded.value = true
  } catch (error) { failure.value = error.message } finally { busy.value = false }
}
function toggle() {
  if (!loaded.value) load()
  else expanded.value = !expanded.value
}
const branches = () => props.branchesOf(props.task.id)
</script>

<template>
  <div class="forest-node" :data-task="task.id" :class="[`is-${task.status}`]">
    <div class="forest-line">
      <router-link :to="`/agents/${agentId}/tasks/${task.id}`" class="forest-title">{{ task.title }}</router-link>
      <span class="forest-tag">{{ t(`board.templates.${task.template}`) }}</span>
      <span class="forest-tag">{{ t('board.reward', { reward: task.reward }) }}</span>
      <span class="forest-tag" :class="`is-${task.status}`">{{ t(`board.status.${task.status}`) }}</span>
      <span v-if="task.holder !== null" class="forest-muted">{{ t('forest.holder', { name: task.holder.name }) }}</span>
      <span v-else-if="task.entrants.length > 0" class="forest-muted">{{ t('forest.entrants', { names: task.entrants.map((item) => item.name).join('、') }) }}</span>
    </div>
    <Button v-if="task.has_children" variant="ghost" size="sm" :disabled="busy" :aria-expanded="expanded" :aria-controls="`forest-children-${task.id}`" @click="toggle">{{ t('okr.children') }}</Button>
    <p v-if="failure" role="alert">{{ failure }}</p>
    <section v-if="expanded" :id="`forest-children-${task.id}`">
    <div v-for="branch in branches()" :key="branch.publisher.agent_id" class="forest-branch">
      <p class="forest-muted forest-by">{{ t('forest.publishedBy', { name: branch.publisher.name }) }}</p>
      <ul>
        <li v-for="child in branch.tasks" :key="child.id" class="forest-edge" :class="`is-${edgeOf(child)}`" :data-edge="edgeOf(child)">
          <TaskForestNode :task="child" :branches-of="branchesOf" :agent-id="agentId" @loaded="emit('loaded', $event)" />
        </li>
      </ul>
    </div>
    <Button v-if="next_before !== null" variant="ghost" size="sm" :disabled="busy" @click="load(true)">{{ t('okr.more') }}</Button>
    </section>
  </div>
</template>

<style scoped>
.forest-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-xs) var(--drama-space-sm);
  padding: var(--drama-space-xs) 0;
}

.forest-title {
  font-weight: var(--drama-font-weight-medium);
  overflow-wrap: anywhere;
}

.forest-tag {
  padding: 0 var(--drama-space-sm);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-full);
  font-size: var(--drama-font-size-xs);
}

.forest-tag.is-settled,
.forest-tag.is-warranty {
  border-color: var(--drama-color-success);
  color: var(--drama-color-success);
}

.forest-tag.is-refunded {
  border-color: var(--drama-color-danger);
  color: var(--drama-color-danger);
}

.forest-muted {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.forest-branch {
  margin-inline-start: var(--drama-space-md);
}

.forest-by {
  margin: var(--drama-space-xs) 0 0;
}

.forest-branch ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* 连线：每个子任务左边一条竖线，已验收实线、进行中虚线。 */
.forest-edge {
  padding-inline-start: var(--drama-space-md);
  border-inline-start: 2px dashed var(--drama-color-border-strong);
}

.forest-edge.is-solid {
  border-inline-start-style: solid;
  border-inline-start-color: var(--drama-color-success);
}

.is-refunded > .forest-line .forest-title,
.is-cancelled > .forest-line .forest-title {
  color: var(--drama-color-text-muted);
  text-decoration: line-through;
}
</style>
